/**
 * Scattergories sheets — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_tabletop_iii`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const scattergoriesSheetsPack: ToilePack = {
    slug: 'scattergories-sheets',
    category: 'tabletop',
    name: 'Scattergories sheets',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'answer', label: 'Answer sheet', subject: 'an answer sheet headed "Letter" over a large empty letter box at the top, a ruled column of 12 numbered answer lines, and three narrow score columns down the right edge'},
        {key: 'categories', label: 'Category list', subject: 'a sheet titled "Categories" with 12 numbered lines reading "A boy\'s name", "Fruits", "Things in a kitchen" and nine more, a small timer drawn at the top'},
        {key: 'letters', label: 'Letter cards', subject: 'a printable sheet of 20 small square letter cards in a 4 by 5 grid with dashed cut lines, each with one large letter from A to T, under a header reading "Letter Cards"'},
    ],
    styles: [
        {key: 'clean', label: 'Clean sheet', style: 'a clean ruled worksheet with thin lines, numbered rows and a bold letter box', palette: 'black ink only', framing: 'the page filled inside a wide margin'},
        {key: 'bright', label: 'Bright family', style: 'a cheerful family-game look with rounded boxes and a bold colored header band', palette: 'teal and orange accents on white', framing: 'the page filled inside a wide margin'},
        {key: 'retro', label: 'Retro board', style: 'a retro board-game look with a thick double border and a bold serif header', palette: 'navy and mustard on cream', framing: 'the page filled inside a wide margin'},
    ],
    directions: ['for kids', 'for teens', 'holiday theme'],
    directionHint: 'try: for kids',
    seo: {
        title: 'Scattergories sheets: answer sheet, categories, letters',
        metaDescription: 'Printable Scattergories sheets in three styles: a player answer sheet with a letter box, a list of 12 categories and a set of letter cards.',
        h1: 'Scattergories sheets: answer sheet, category list, letter cards',
        tagline: 'Nine sheets: an answer sheet, a category list and letter cards, in three styles.',
        intro: 'Families and classes print an answer sheet for each player in the list game where every answer starts with one letter. This pack shows an answer sheet with a letter box and 12 numbered lines, a category list, and 20 letter cards from A to T. Each comes as a clean ruled worksheet, a bright family page or a retro board look. Type a category set, such as for kids, and the sheet keeps its lines.',
        useCases: [
            'a family game night',
            'a class list game in a lesson',
            'a party game with a sheet for each player',
            'a set of letter cards for each round',
        ],
        faq: [
            {q: 'What do the nine pictures show?', a: 'Three readings in three styles: a player answer sheet, a category list, and a sheet of letter cards.'},
            {q: 'How many answer lines are on the sheet?', a: '12 numbered answer lines, with three narrow score columns down the right edge.'},
            {q: 'What are the letter cards for?', a: 'Each round one letter is drawn and every answer starts with it; the 20 cards hold one large letter each, from A to T.'},
            {q: 'Can I change the categories?', a: 'Yes. The category list is a sheet of 12 numbered lines, and a set for kids or a holiday theme can be asked for.'},
        ],
    },
}
